import React from 'react';
import PropTypes from 'prop-types';
import $ from 'jquery';
import _ from 'underscore';
import '@bingads-webui/kendo/kendo.datepicker';
import MalformedInputError from './malformed-input-error';
import monthEmpty from './month-empty.pug';
import input from './input.pug';

function convertDateBewteenTimeZones(i18n, date, {
  from,
  to,
}) {
  return i18n.parseDate(i18n.formatDate(date, { skeleton: 'yMd', timeZone: from }), {
    timeZone: to,
  });
}

function getMachineToday(i18n) {
  return convertDateBewteenTimeZones(i18n, new Date(), {
    to: null,
  });
}

function amendDate(i18n, date) {
  return convertDateBewteenTimeZones(i18n, date, { from: null });
}

// Kendo doesn't reconginize format with "y", which is similar to "yyyy"
// See:
// https://docs.telerik.com/kendo-ui/framework/globalization/dateformatting#custom
function toKendoFormat(format) {
  const match = /\by\b/.exec(format);

  if (match) {
    const formatArr = format.split('');

    formatArr.splice(match.index, 1, 'yyyy');
    return formatArr.join('');
  }

  return format;
}

function propsToOptions({
  i18n,
  max,
  min,
  value,
  skeleton,
}) {
  const formatDate = date => (_.isDate(date) ? i18n.formatDate(date, { skeleton }) : date);

  return {
    culture: i18n.getLocale(),
    format: toKendoFormat(i18n.getCldr().main(`dates/calendars/gregorian/dateTimeFormats/availableFormats/${skeleton}`)),
    max: formatDate(max),
    min: formatDate(min),
    value: formatDate(value),
  };
}

function getRelevantProps({
  i18n,
  max,
  min,
  value,
  placeholder,
  skeleton,
  disabled,
}) {
  return {
    i18n,
    max,
    min,
    value,
    placeholder,
    skeleton,
    disabled,
  };
}

function normalizePlaceholder(placeholder, { skeleton, raw }) {
  if (_.isString(placeholder)) {
    return placeholder;
  }

  if (!placeholder) {
    return '';
  }

  switch (placeholder.type) {
    case 'skeleton':
      return skeleton;
    case 'raw':
      return raw;
    default:
      throw new RangeError(`Unknown placeholder prop: ${JSON.stringify(placeholder)}`);
  }
}

export class KendoDatePicker extends React.Component {
  static propTypes = {
    value: PropTypes.instanceOf(Date),
    max: PropTypes.instanceOf(Date),
    min: PropTypes.instanceOf(Date),
    placeholder: PropTypes.oneOfType([
      PropTypes.shape({
        type: PropTypes.oneOf(['skeleton', 'raw']),
      }),
      PropTypes.string,
    ]),
    skeleton: PropTypes.string,
    i18n: PropTypes.shape({
      getLocale: PropTypes.func,
      getCldr: PropTypes.func,
    }).isRequired,
    onChange: PropTypes.func,
    onError: PropTypes.func,
    disabled: PropTypes.bool,
    ariaLabelledby: PropTypes.string,
  }

  static defaultProps = {
    value: null,
    max: undefined,
    min: undefined,
    placeholder: null,
    skeleton: 'yMd',
    onChange: _.noop,
    onError: _.noop,
    disabled: false,
    ariaLabelledby: undefined,
  }

  componentDidMount() {
    const {
      i18n,
      max,
      min,
      placeholder,
      skeleton,
      value,
      disabled,
      ariaLabelledby,
    } = this.props;
    const options = propsToOptions({
      i18n,
      max,
      min,
      value,
      skeleton,
    });

    const $container = $(this.container);
    $container.html(input({
      placeholder: normalizePlaceholder(placeholder, {
        skeleton,
        raw: options.format,
      }),
    }));
    const $el = $container.find('input');
    $el[0].disabled = disabled;
    $el.click(this.onClick);

    const {
      setValue,
      setError,
    } = this;

    $el.kendoDatePicker(_.defaults({
      animation: false,
      footer: false,
      change() {
        const newValue = this.value();

        if (_.isDate(newValue)) {
          setValue(amendDate(i18n, newValue));
        } else {
          const elementVal = this.element.val();
          if (elementVal === '') {
            setValue(null);
          } else {
            setError(new MalformedInputError(elementVal));
          }
        }
      },
      open: () => {
        // Fix Bug 3115278: Bad gradient effect of the date picker
        const { calendar } = this.widget.dateView;
        if (calendar && calendar.setOptions) {
          calendar.setOptions({ animation: false });
        }
      },
      close: () => {
        const element = _.get(this.widget, ['element', 0]);
        if (element && _.isFunction(element.focus)) {
          element.focus();
        }
      },
      month: {
        empty: monthEmpty(),
      },
    }, options));

    $el.addClass('form-control');
    $el.attr({
      'aria-labelledby': ariaLabelledby,
    });

    this.widget = $el.data('kendoDatePicker');
  }

  componentDidUpdate(prevProps) {
    if (!_.isEqual(getRelevantProps(this.props), getRelevantProps(prevProps))) {
      const {
        i18n,
        max,
        min,
        value,
        placeholder,
        skeleton,
        disabled,
      } = this.props;
      const options = propsToOptions({
        i18n,
        max,
        min,
        value,
        skeleton,
      });

      this.widget.setOptions(_.omit(options, 'value'));
      // setOptions doesn't correctly handle value when value is falsy
      this.widget.value(options.value);

      this.widget.element.prop('disabled', disabled);
      this.widget.element.prop('placeholder', normalizePlaceholder(placeholder, {
        skeleton,
        raw: options.format,
      }));
    }
  }

  componentWillUnmount() {
    if (this.widget) {
      this.widget.destroy();
    }
  }

  onClick = () => {
    const { widget } = this;
    widget.open();

    const { i18n } = this.props;
    const { dateView } = widget;
    // use i18n time zone's today instead of machine's one
    dateView.current(getMachineToday(i18n));
    dateView.div.find('.k-today').removeClass('k-today');
    dateView.div.find('.k-state-focused').addClass('k-today');
    dateView.div.find('.k-nav-prev').attr({
      'aria-label': 'Previous',
    })
    dateView.div.find('.k-nav-next').attr({
      'aria-label': 'Next',
    })
    dateView.div.find('.k-calendar').attr({
      tabindex: '0',
    })
  }

  setValue = (value) => {
    this.props.onChange(value);
  }

  setError = (err) => {
    this.props.onError(err);
  }

  ref = (container) => {
    this.container = container;
  }

  render() {
    return (
      <div
        className="kendo-date-picker"
        ref={this.ref}
      />
    );
  }
}
